import React from "react";
import { Collapse, CollapseProps, List, Typography } from "antd";
import {
  CaretRightOutlined,
  WifiOutlined,
  ClockCircleOutlined,
  FullscreenOutlined,
  ScissorOutlined,
  BookOutlined,
  ApiOutlined,
  CustomerServiceOutlined,
  TeamOutlined,
  DesktopOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { INSTRUCTION_PANELS } from "../../constants/interview-constants";

// Icon mapping
const iconMap: Record<string, React.ReactNode> = {
  // General Guidelines icons
  WifiOutlined: <WifiOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  FullscreenOutlined: <FullscreenOutlined />,
  ScissorOutlined: <ScissorOutlined />,
  ApiOutlined: <ApiOutlined />,
  CustomerServiceOutlined: <CustomerServiceOutlined />,
  // Prohibited Activities icons
  TeamOutlined: <TeamOutlined />,
  BookOutlined: <BookOutlined />,
  DesktopOutlined: <DesktopOutlined />,
  SafetyOutlined: <SafetyOutlined />,
};

const panelStyle: React.CSSProperties = {
  marginBottom: 16,
  borderRadius: 8,
  border: "1px solid #E8E8E8",
  backgroundColor: "#FBFBFB",
};

interface InstructionItem {
  text: string;
  icon: string;
}

// Default export renders the JSON inside antd Collapse (accordion)
const InstructionList: React.FC = () => {
  const getInstructionLabel = (label: string) => {
    return (
      <div>
        <Typography.Title
          level={5}
          style={{
            margin: 0,
            textAlign: "start",
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          {label}
        </Typography.Title>
      </div>
    );
  };

  const getInstructionChildren = (
    lead: string | null,
    items: InstructionItem[],
    isProhibited: boolean
  ) => {
    return (
      <div className="instruction-children-container">
        {lead && (
          <div className="instruction-lead-text">
            <Typography.Text type="warning" strong>
              {lead}
            </Typography.Text>
          </div>
        )}
        <List
          bordered={false}
          dataSource={items}
          renderItem={(item) => (
            <List.Item className={`instruction-list-item ${isProhibited ? "instruction-list-item--prohibited" : ""}`}>
              <span className={`instruction-list-item-icon ${isProhibited ? "instruction-list-item-icon--prohibited" : ""}`}>
                {iconMap[item.icon] || <span className="instruction-list-item-bullet"></span>}
              </span>
              <span className="instruction-list-item-text">{item.text}</span>
            </List.Item>
          )}
        />
      </div>
    );
  };

  const collapseItems: CollapseProps["items"] = INSTRUCTION_PANELS.map(
    (panel) => {
      return {
        key: panel.key,
        label: getInstructionLabel(panel.label),
        children: getInstructionChildren(
          panel.lead || null,
          panel.items as InstructionItem[],
          panel.key === "prohibited"
        ),
        style: panelStyle,
      };
    }
  );

  // Get all keys to expand all sections by default
  const allKeys = collapseItems.map((item) => item.key as string);

  return (
    <div className="instruction-collapse-wrapper">
      <Collapse
        bordered={false}
        defaultActiveKey={allKeys}
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        items={collapseItems}
        style={{ backgroundColor: "white" }}
      />
    </div>
  );
};

export default InstructionList;
